import React from 'react';
import PageBar from '../../components/PageBar';
import { useToast } from '../../components/ToastContext';
import { THEMES, useAppearance, saveAppearance } from '../../theme/appearance';

/*
  APPEARANCE: a person picks their theme, and light, dark or automatic.

  Theme system, phase 5, 14 September 2026; docs/THEME-SYSTEM.md sections 2
  and 3, and the implementation brief's phase 5. It is a page in the Settings
  area of EVERY job level's menu (src/lib/menu.jsx), reps and buyers included,
  and deliberately not in the menu under a person's name.

  - **Live.** Choosing applies at once, to the whole app, and saves to the
    person's profile. If the save does not stick the choice goes back and a
    message says so (src/theme/appearance.js does the saving and the undo).
  - **Each theme shows both of its versions**, light and dark, drawn from the
    theme's own colours and artwork rather than from a picture of them: a
    thumbnail carries data-theme and data-mode itself, so public/theme/themes.css
    styles it exactly as it would style the app.
  - **The brand's ten, in the guide's 8.3 order** since rebrand step 6, 17
    September 2026. Each thumbnail carries `data-theme` and `data-color-mode`
    itself, so `src/theme/brandThemes.css` colours it exactly as it colours the
    app, and a small still drawing of that theme's artwork sits behind it.
    The moving artwork itself is `src/theme/brandArt.js`, drawn behind the page.
  - **Keyboard and screen readers.** Both controls are real radio buttons, so
    arrow keys move through them, Tab moves between them, and a screen reader
    says which is chosen. The chosen theme is marked with a tick and the word
    "Selected" as well as an outline, never by colour alone.
*/
export default function Appearance() {
  const { known, theme, mode, deviceMode, showingMode } = useAppearance();
  const toast = useToast();

  async function choose(patch) {
    const ok = await saveAppearance(patch);
    if (!ok) toast.error('That could not be saved, so it has been put back. Check the connection and try again.');
  }

  const modeNote = mode === 'auto'
    ? `Automatic is showing ${showingMode} right now, because this device is set to ${deviceMode}.`
    : `Staying ${mode} whatever this device is set to.`;

  return (
    <>
      <PageBar title="Appearance" subtitle="Your theme follows you to every device and every site" />
      <div className="ap-page">
        <section className="card ap-section" aria-labelledby="ap-mode-title">
          <h2 id="ap-mode-title" className="ap-title">Light or dark</h2>
          <div className="ap-modes" role="radiogroup" aria-labelledby="ap-mode-title">
            {[['auto', 'Automatic'], ['light', 'Light'], ['dark', 'Dark']].map(([key, label]) => (
              <label key={key} className={'ap-mode' + (mode === key ? ' on' : '')}>
                <input
                  type="radio" name="oh-appearance-mode" value={key}
                  checked={mode === key} disabled={!known}
                  onChange={() => choose({ mode: key })}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          <p className="ap-note" aria-live="polite">{modeNote}</p>
        </section>

        <section className="card ap-section" aria-labelledby="ap-theme-title">
          <h2 id="ap-theme-title" className="ap-title">Theme</h2>
          <p className="ap-note">It changes straight away. Each shows its light and its dark version.</p>
          <div className="ap-grid">
            {THEMES.map(t => {
              const on = theme === t.slug;
              return (
                <label key={t.slug} className={'ap-theme' + (on ? ' on' : '')}>
                  <input
                    type="radio" name="oh-appearance-theme" value={t.slug}
                    checked={on} disabled={!known}
                    onChange={() => choose({ theme: t.slug })}
                    aria-describedby={on ? 'ap-selected' : undefined}
                  />
                  <span className="ap-thumbs" aria-hidden="true">
                    <Thumb slug={t.slug} mode="light" />
                    <Thumb slug={t.slug} mode="dark" />
                  </span>
                  <span className="ap-theme-foot">
                    <span className="ap-theme-name">{t.name}</span>
                    {on && (
                      <span className="ap-theme-tick">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="4 12 10 18 20 6" /></svg>
                        Selected
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
          <span id="ap-selected" hidden>Selected</span>
        </section>
      </div>
    </>
  );
}

/* One version of one theme, small: its page colour and a still of its artwork,
   with a card, a line of words and a button drawn from the theme's own roles. */
function Thumb({ slug, mode }) {
  return (
    <span className="ap-thumb" data-theme={slug} data-mode={mode} data-color-mode={mode}>
      <span className="ap-thumb-art" data-art={slug} />
      <span className="ap-thumb-card">
        <span className="ap-thumb-line" />
        <span className="ap-thumb-line short" />
        <span className="ap-thumb-button" />
      </span>
      <span className="ap-thumb-mode">{mode === 'light' ? 'Light' : 'Dark'}</span>
    </span>
  );
}
